import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import swal from 'sweetalert';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { BalanceSheetService } from '../../../service/account/balance-sheet.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GenerateKeyResultComponent } from '../generate-key-result/generate-key-result.component';
import { HttpClient } from '@angular/common/http';
import { EnterPublicKeyComponent } from '../enter-public-key/enter-public-key.component';

@Component({
  selector: 'app-developer-keys',
  standalone: true,
  imports: [
    MatDialogModule,
    FormsModule,
    CommonModule,
    TranslatePipe,
    GenerateKeyResultComponent,
  ],
  templateUrl: './developer-keys.component.html',
  styleUrl: './developer-keys.component.scss',
})
export class DeveloperKeysComponent implements OnInit {
  showKeys: boolean[] = [];
  showKey = false;
  apiKey: string | null = null;
  copiedIndex: number | null = null;
  copiedDefault = false;
  partnerSymetricAlg: boolean;
  keyFile!: Blob;
  showKeyModal: boolean = false;
  activeTab: string = 'keys';
  public user: any;
  javaCode: string = '';
  pythonCode: string = '';
  phpCode: string = '';
  selectedTab: 'java' | 'python' | 'php' = 'java';

  constructor(
    public dialogRef: MatDialogRef<DeveloperKeysComponent>,
    private balanceSheetService: BalanceSheetService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translate: TranslateService,
    private dialog: MatDialog,
    private http: HttpClient,
  ) {
    this.showKeys = Array(data.apiKeys.length).fill(false);
    this.partnerSymetricAlg = data.partnerSymetricAlg;
    this.user = JSON.parse(localStorage.getItem('user') as string);
  }

  ngOnInit(): void {
    this.loadCodeFiles();
  }

  loadCodeFiles() {
    this.http
      .get('assets/code/java-jwt.txt', { responseType: 'text' })
      .subscribe((data) => (this.javaCode = data));

    this.http
      .get('assets/code/python-jwt.txt', { responseType: 'text' })
      .subscribe((data) => (this.pythonCode = data));

    this.http
      .get('assets/code/php-jwt.txt', { responseType: 'text' })
      .subscribe((data) => (this.phpCode = data));
  }

  selectTab(tab: 'java' | 'python' | 'php') {
    this.selectedTab = tab;
  }

  toggleKey(index: number): void {
    this.showKeys[index] = !this.showKeys[index];
  }

  close(): void {
    this.dialogRef.close();
  }

  toggleKeyVisibility(index: number): void {
    this.showKeys[index] = !this.showKeys[index];
  }

  toggleDefaultKeyVisibility() {
    this.showKey = !this.showKey;
    this.apiKey = this.data.defaultKey;
  }

  copyDefaultKey(value: string): void {
    navigator.clipboard.writeText(value).then(() => {
      this.copiedDefault = true;
      setTimeout(() => (this.copiedDefault = false), 2000);
    });
  }

  copyKey(value: string, index?: number): void {
    navigator.clipboard.writeText(value).then(() => {
      if (index !== undefined) {
        this.copiedIndex = index;
        setTimeout(() => (this.copiedIndex = null), 2000);
      }
    });
  }

  resetKey(): void {
    console.log('Key reset');
  }

  resetApiKey() {
    this.balanceSheetService.getResetTellerKey().subscribe({
      next: (res) => {
        if (res?.success) {
          // Update the data shown on modal dynamically
          this.data.defaultKey = res.apiKey;
          const apiKeyItem = this.data.apiKeys.find(
            (key: any) => key.name === 'API Key',
          );
          if (apiKeyItem) {
            apiKeyItem.value = res.apiKey;
          }
          swal(
            this.translate.instant('ALERT.SUCCESS'),
            this.translate.instant('ALERT.API_RESET.SUCCESSFUL'),
            'success',
          );
          this.close();
        } else {
          swal(
            this.translate.instant('ALERT.ERROR'),
            res.message || this.translate.instant('ALERT.API_RESET.FAILED'),
            'error',
          );
        }
      },
      error: (err) => {
        swal(
          this.translate.instant('ALERT.ERROR'),
          err?.message || this.translate.instant('ALERT.SOMETHING_WRONG'),
          'error',
        );
      },
    });
  }

  async generateNewKey() {
    const confirm = await swal({
      title: 'Do you want to Generate your Key Pair Now?',
      text: 'After Generating the Key Pair, you will have to download and save the Private Key securely',
      icon: 'warning',
      buttons: ['No', 'Yes,Generate'],
      dangerMode: true,
    });

    if (!confirm) return;

    // this.callGenerateKeyAPI();
    this.balanceSheetService
      .getGeneratedPublicAndPrivateKey()
      .subscribe((res: Blob) => {
        this.keyFile = res;
        this.showKeyModal = true;
      });
  }

  callGenerateKeyAPI() {
    this.balanceSheetService.getGeneratedPublicAndPrivateKey().subscribe({
      next: (response: Blob) => {
        const blob = new Blob([response], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'private_key.pem';
        a.click();

        window.URL.revokeObjectURL(url);

        console.log('Private key downloaded successfully');
      },
      error: (error) => {
        console.error('Error generating keys:', error);
      },
    });
  }

  openPublicKeyModal() {
    const dialogRef = this.dialog.open(EnterPublicKeyComponent, {
      width: '550px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log('Received Public Key:', result);
      }
    });
  }
}
